<ng-template let-extraClass="extraClass" #topNav>
  @if (!windowService?.isDesktopRes) {
    <tidy-top-nav
      [class]="extraClass"
      [title]="'Legal / Compliance'"
      [canGoBack]="true"
      [customBack]="fromGettingStarted ? 'getting-started' : null" />
  }
</ng-template>

<ng-container *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<ng-template #content>
  <tidy-wrapper>
    @if (!loaded) {
      <div class="loading-container">
        <tidy-loader />
      </div>
    }

    @if (loaded) {
      <form [formGroup]="itemForm" novalidate action="#">
        <div class="wizard-progress-bar">
          <div class="wizard-progress-fill" [style.width.%]="progressPercent"></div>
        </div>

        @if (currentStep === 1) {
          <div class="wizard-step-header">
            <tidy-text [title]="'What do you want TIDY to track?'" />
          </div>

          <div class="wizard-cards">
            <tidy-card
              class="wizard-card"
              (click)="selectMethod('nothing')">
              <div class="wizard-card-radio">
                  <span class="wizard-card-radio-inner"></span>
                </div>
              <div class="wizard-card-text">
                <tidy-text [body]="'Nothing, I will do compliance'" />
              </div>
            </tidy-card>

            <tidy-card
              class="wizard-card"
              (click)="selectMethod('track')">
              <div class="wizard-card-radio">
                  <span class="wizard-card-radio-inner"></span>
                </div>
              <div class="wizard-card-text">
                <tidy-text [body]="'Track compliance through TIDY'" />
              </div>
            </tidy-card>
          </div>
        }

        @if (currentStep === 2 && itemForm.value.method === 'nothing') {
          <div class="wizard-step-header">
            <tidy-text [title]="'Notes for Concierge (Optional)'" />
          </div>
          <div class="wizard-content">
            <tidy-row>
              <tidy-textarea
                [label]="'Anything we should know about how you handle compliance?'"
                [icon]="'assets/svg/create.svg'"
                [submitted]="submitted"
                [errorMessage]="'Please enter a description'"
                [form]="itemForm.controls.body"
                formControlName="body" />
            </tidy-row>

            <div class="wizard-nav-row">
              <tidy-button
                [text]="'Finish'"
                [action]="finish"
                class="primary" />
            </div>
          </div>
        }

        @if (currentStep === 2 && itemForm.value.method === 'track') {
          <div class="wizard-step-header">
            <tidy-text [title]="'Review Compliance Suggestions'" />
          </div>

          @if (isRunningCheck) {
            <div class="wizard-content">
              <tidy-alert class="black-background">
                <tidy-row>
                  <tidy-text [header]="'Generating compliance suggestions'" />
                </tidy-row>
                <tidy-row class="extra-top-padding vertical-align-center">
                  <ion-spinner name="lines" style="margin-right: 10px;" />
                  <tidy-text
                    [helper]="'Analyzing compliance requirements. This may take 2-10 minutes...'" />
                </tidy-row>
              </tidy-alert>
            </div>
          }

          @if (!isRunningCheck) {
            <div class="wizard-step-header">
              <tidy-text [helper]="'Please review the suggestions below.'" />
            </div>

            <div class="wizard-content">
              @if (complianceCheckStatus === 'failed') {
                <tidy-row class="extra-bottom-padding">
                  <tidy-text
                    [body]="'Compliance check failed. Please try running the report again.'" />
                </tidy-row>
                <tidy-row>
                  <tidy-small-button
                    [text]="'Run Compliance Report'"
                    (buttonClick)="runComplianceReport()" />
                </tidy-row>
              }

              @if (bucketGroups.length > 0) {
                <tidy-row class="compliance-select-all">
                  <tidy-checkbox
                    [initValue]="allSuggestionsSelected"
                    [items]="[{viewValue: 'Add all suggestions', value: true}]"
                    (checkedChange)="toggleAllSuggestions()" />
                </tidy-row>

                @for (group of bucketGroups; track group.bucket) {
                  <tidy-card>
                    <div class="compliance-bucket-header" [class.compliance-bucket-expanded]="group.expanded" (click)="toggleBucket(group)">
                      <div class="compliance-bucket-toggle">
                        <div class="compliance-chevron-icon" [class.compliance-chevron-expanded]="group.expanded">▶</div>
                        <tidy-text [heavyBody]="group.label + ' (' + group.items.length + ')'" />
                      </div>
                    </div>

                    @if (group.expanded) {
                      @for (item of group.items; track item.id) {
                        <div class="compliance-item">
                          <div class="compliance-item-row">
                            <tidy-checkbox
                              [initValue]="isItemSelected(item.id)"
                              [items]="[{viewValue: '', value: true}]"
                              (checkedChange)="toggleItemSelected(item.id)" />
                            <div
                              class="compliance-item-content"
                              [class.compliance-item-content--static]="!hasItemDetail(item)"
                              (click)="hasItemDetail(item) && toggleItem(item.id)">
                              <tidy-text [body]="item.name || item.what_to_do || 'Compliance item'" />
                              @if (hasItemDetail(item)) {
                                <div class="compliance-item-expand" [class.compliance-chevron-expanded]="isItemExpanded(item.id)">▶</div>
                              }
                            </div>
                          </div>
                          @if (isItemExpanded(item.id) && hasItemDetail(item)) {
                            <div class="compliance-item-detail">
                              @if (item.name && item.what_to_do) {
                                <tidy-text [helper]="item.what_to_do" />
                              }
                              @if (item.how_to_do_it) {
                                <tidy-text [helper]="item.how_to_do_it" />
                              }
                            </div>
                          }
                        </div>
                      }
                    }
                  </tidy-card>
                }
              }

              <div class="wizard-nav-row">
                <tidy-button
                  [text]="'Next'"
                  [action]="goToNextStep"
                  class="primary" />
              </div>
            </div>
          }
        }

        @if (currentStep === 3 && itemForm.value.method === 'track') {
          <div class="wizard-step-header">
            <tidy-text [title]="'Notes for Concierge (Optional)'" />
          </div>
          <div class="wizard-content">
            <tidy-row>
              <tidy-textarea
                [label]="'Anything we should know about your compliance?'"
                [icon]="'assets/svg/create.svg'"
                [submitted]="submitted"
                [errorMessage]="'Please enter a description'"
                [form]="itemForm.controls.body"
                formControlName="body" />
            </tidy-row>

            <div class="wizard-nav-row">
              <tidy-button
                [text]="'Finish'"
                [action]="finish"
                class="primary" />
            </div>
          </div>
        }
      </form>
    }
  </tidy-wrapper>
</ng-template>

@if (!windowService?.isDesktopRes) {
  <app-scroll-wrapper [templateContent]="content" />
} @else {
  <ng-container *ngTemplateOutlet="content" />
}
